import { useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChevronDown,
  ChevronUp,
  EllipsisVertical,
  GripVertical,
  Plus,
  Trash2,
} from "lucide-react";
import t from "@/lang/lang";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import { cn } from "@/shared/lib/utils";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { useAppDispatch } from "@/shared/hooks/redux-hooks";
import { toast } from "sonner";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import { Button } from "@/shared/components/ui/button";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellDecimalInput,
  ETableCellInput,
  ETableFooter,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import {
  useVbsPricingByAreaShowQuery,
  useVbsPricingByAreaPutMutation,
  updatePricingByAreaCache,
  useVbsPricingByAreaUpdateUseCalculationMutation,
  updateVbsPricingByAreaCache,
} from "@/services/backend/vbs/pricing/api/pricing-by-area-api";
import {
  addArea,
  deleteArea,
  PricingByArea,
  PricingByAreaArea,
  reorderAreas,
  updateArea,
  updateUseCalculation,
} from "../types/pricing-by-area";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  joinFieldName,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";

function euroFmt(value: number): string {
  return `${value.toLocaleString("de", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

export function PricingByAreaCard({
  pricingByAreaId,
  disabled = false,
}: {
  pricingByAreaId: string;
  disabled?: boolean;
}) {
  const { data, isLoading, error } = useVbsPricingByAreaShowQuery({
    id: pricingByAreaId,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Bereichsaufteilung")}</CardTitle>
        <CardAction>
          <UseCalculationToggle
            pricingByAreaId={pricingByAreaId}
            disabled={disabled}
          />
        </CardAction>
      </CardHeader>
      <CardContent>
        {error && <RTKQueryErrorAlert error={error} showValidationErrors />}
        {isLoading && <CardContentSkeleton />}
        {data && (
          <PricingByAreaCardContent pricingByArea={data} disabled={disabled} />
        )}
      </CardContent>
    </Card>
  );
}

function PricingByAreaCardContent({
  pricingByArea,
  disabled = false,
}: {
  pricingByArea: PricingByArea;
  disabled?: boolean;
}) {
  const [set] = useOptimisticDebouncedMutation(
    pricingByArea,
    updateVbsPricingByAreaCache,
    useVbsPricingByAreaPutMutation(),
  );

  return (
    <PricingByAreaTable
      data={pricingByArea}
      onChange={(data) => set(data)}
      disabledProp={disabled}
    />
  );
}

function PricingByAreaTable({
  data,
  onChange,
  disabledProp = false,
}: {
  data: PricingByArea;
  onChange: (data: PricingByArea) => void;
  disabledProp?: boolean;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const disabled = data.useCalculation || disabledProp;

  function handleDragEnd(event: DragEndEvent) {
    if (disabled) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = data.areas.findIndex((a) => a.id === active.id);
    const newIndex = data.areas.findIndex((a) => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onChange(reorderAreas(data, arrayMove(data.areas, oldIndex, newIndex)));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={data.areas.map((a) => a.id)}
        strategy={verticalListSortingStrategy}
      >
        <ETable>
          <ETableHeader>
            <ETableRow>
              <ETableHead className="w-10" />
              <ETableHead>{t("Bereich")}</ETableHead>
              <ETableHead className="text-right">
                {t("Grundbehandlung")}
              </ETableHead>
              <ETableHead className="text-right">
                {t("Verkaufsrabatt")}
              </ETableHead>
              <ETableHead className="text-right">
                {t("Sonderrabatt")}
              </ETableHead>
              <ETableHead className="text-right">
                {t("Grundbeh. abzgl. Rabatte")}
              </ETableHead>
              <ETableHead className="text-right">{t("Nebenkosten")}</ETableHead>
              <ETableHead className="text-right">{t("Endpreis")}</ETableHead>
              <ETableHead className="w-10" />
            </ETableRow>
          </ETableHeader>
          <ETableBody className="[&>tr:last-child>td]:border-b">
            {data.areas.length === 0 && (
              <ETableRow>
                <ETableCell colSpan={9} className={cn("!py-2 text-center")}>
                  <div className="flex items-center justify-center gap-2">
                    <span>
                      {t("Es sind noch keine Bereiche hinzugefügt worden.")}
                    </span>
                    <Button
                      onClick={() => onChange(addArea(data))}
                      variant="secondary"
                      size="sm"
                      disabled={disabled}
                    >
                      <Plus />
                    </Button>
                  </div>
                </ETableCell>
              </ETableRow>
            )}
            {data.areas.map((area, index) => (
              <AreaRow
                key={area.id}
                index={index}
                area={area}
                onAreaChange={(updated) =>
                  onChange(updateArea(data, area.id, updated))
                }
                byArea={data}
                onByAreaChange={onChange}
                disabled={disabled}
              />
            ))}
          </ETableBody>
          <ETableFooter>
            <ETableRow>
              <ETableCell />
              <ETableCell>{t("Gesamt")}</ETableCell>
              <ETableCell className="text-right">
                {euroFmt(data.totalTreatmentFinalPrice)}
              </ETableCell>
              <ETableCell className="text-right">
                {euroFmt(data.totalRelativeDiscount)}
              </ETableCell>
              <ETableCell className="text-right">
                {euroFmt(data.totalAbsoluteDiscount)}
              </ETableCell>
              <ETableCell className="text-right">
                {euroFmt(data.totalDiscountedTreatmentFinalPrice)}
              </ETableCell>
              <ETableCell className="text-right">
                {euroFmt(data.totalIncidentalCostTotalAmount)}
              </ETableCell>
              <ETableCell className="text-right">
                {euroFmt(data.totalAreaFinalPrice)}
              </ETableCell>
              <ETableCell />
            </ETableRow>
          </ETableFooter>
        </ETable>
      </SortableContext>
    </DndContext>
  );
}

function AreaRow({
  area,
  onAreaChange,
  byArea,
  onByAreaChange,
  index,
  disabled,
}: {
  area: PricingByAreaArea;
  onAreaChange: (update: Partial<PricingByAreaArea>) => void;
  byArea: PricingByArea;
  onByAreaChange: (byArea: PricingByArea) => void;
  index: number;
  disabled: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: area.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ETableRow ref={setNodeRef} style={style}>
      <ETableCell>
        <button
          {...attributes}
          {...listeners}
          type="button"
          className="hover:bg-muted cursor-grab rounded p-1 disabled:cursor-default disabled:opacity-50"
          disabled={disabled}
        >
          <GripVertical className="text-muted-foreground h-4 w-4" />
        </button>
      </ETableCell>
      <ETableCellInput
        value={area.area}
        onChange={(e) => onAreaChange({ area: e.target.value })}
        disabled={disabled}
        fieldName={joinFieldName("areas", area.id, "area")}
      />
      <ETableCellDecimalInput
        value={area.treatmentFinalPrice}
        onValueChange={(treatmentFinalPrice) =>
          onAreaChange({ treatmentFinalPrice })
        }
        disabled={disabled}
      />
      <ETableCellInput
        type="number"
        value={area.relativeDiscount}
        onChange={(e) =>
          onAreaChange({ relativeDiscount: Number(e.target.value) })
        }
        disabled={disabled}
      />
      <ETableCellInput
        type="number"
        value={area.absoluteDiscount}
        onChange={(e) =>
          onAreaChange({ absoluteDiscount: Number(e.target.value) })
        }
        disabled={disabled}
      />
      <ETableCell className={cn("text-right")}>
        {euroFmt(area.discountedTreatmentFinalPrice)}
      </ETableCell>
      <ETableCellDecimalInput
        value={area.incidentalCostTotalAmount}
        onValueChange={(incidentalCostTotalAmount) =>
          onAreaChange({ incidentalCostTotalAmount })
        }
        disabled={disabled}
      />
      <ETableCell className={cn("text-right")}>
        {euroFmt(area.areaFinalPrice)}
      </ETableCell>
      <ETableCell>
        <AreaRowOptionsDropdown
          area={area}
          rowIndex={index}
          byArea={byArea}
          onByAreaChange={onByAreaChange}
          disabled={disabled}
        />
      </ETableCell>
    </ETableRow>
  );
}

function UseCalculationToggle({
  pricingByAreaId,
  disabled = false,
}: {
  pricingByAreaId: string;
  disabled?: boolean;
}) {
  const { data } = useVbsPricingByAreaShowQuery({ id: pricingByAreaId });

  const dispatch = useAppDispatch();
  const [patch, { isLoading, error, reset }] =
    useVbsPricingByAreaUpdateUseCalculationMutation();

  const onCheckedChange = (checked: boolean) => {
    if (!data) return;
    const updated = updateUseCalculation(data, checked);
    // Optimistically update the cache for immediate UI feedback.
    dispatch(updatePricingByAreaCache(updated));
    // Run the mutation to update the backend.
    patch({ id: pricingByAreaId, useCalculation: checked });
  };

  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error));
      reset();
    }
  });

  if (!data) return null;

  return (
    <div className="flex items-center space-x-2">
      <Label htmlFor="pricing-by-area-use-calculation">
        {t("Kalkulation nutzen")}
      </Label>
      <Switch
        id="pricing-by-area-use-calculation"
        checked={data.useCalculation}
        onCheckedChange={onCheckedChange}
        disabled={isLoading || disabled}
      />
    </div>
  );
}

function AreaRowOptionsDropdown({
  area,
  rowIndex,
  byArea,
  onByAreaChange,
  disabled,
}: {
  area: PricingByAreaArea;
  rowIndex: number;
  byArea: PricingByArea;
  onByAreaChange: (byArea: PricingByArea) => void;
  disabled: boolean;
}) {
  const { resetFields } = useErrValidationFailedContext();

  const doDelete = () => {
    resetFields(joinFieldName("areas", area.id));
    onByAreaChange(deleteArea(byArea, area.id));
  };

  const insertRowBelow = () => {
    onByAreaChange(addArea(byArea, rowIndex + 1));
  };

  const insertRowAbove = () => {
    onByAreaChange(addArea(byArea, rowIndex));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button variant="ghost" size="sm" className="rounded-none">
          <EllipsisVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("Aktionen")}</DropdownMenuLabel>
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={insertRowAbove}>
            {t("Zeile darüber einfügen")}
            <DropdownMenuShortcut>
              <ChevronUp />
            </DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={insertRowBelow}>
            {t("Zeile darunter einfügen")}
            <DropdownMenuShortcut>
              <ChevronDown />
            </DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={doDelete}>
            {t("Zeile löschen")}
            <DropdownMenuShortcut>
              <Trash2 />
            </DropdownMenuShortcut>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
